import { ChangeEvent, useEffect, useState, useRef, forwardRef, useImperativeHandle } from 'react'
import { Box, Button, InputAdornment, TextField, useTheme } from '@mui/material'
import SendIcon from '@mui/icons-material/Send'

import { WebChatBotHistory } from './chat-history'
import {
  useClearUserChatHistory,
  useGeneratedWebChatbotResponce,
  useGetWebChatbotHistory,
  useItemsRemoveConfirm,
} from 'src/hooks'
import { useStyles } from '../styles'
import { useTranslation } from 'react-i18next'

interface IProps {
  messageValue?: string
}

export const WebChatbot = forwardRef(({ messageValue }: IProps, ref) => {
  const [t] = useTranslation()
  const [message, setMessage] = useState(messageValue || '')

  const messageRef = useRef('')

  const { setRemoveConfirm } = useItemsRemoveConfirm()
  const { data, isLoading } = useGetWebChatbotHistory()
  const { mutateAsync, isLoading: isGenerateResponceLoading } = useGeneratedWebChatbotResponce()
  const { mutateAsync: clearHistory, isLoading: isClearHistoryLoading } = useClearUserChatHistory()

  const theme = useTheme()
  const classes = useStyles()

  useEffect(() => {
    setMessage(messageValue || '')
  }, [messageValue])

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setMessage(e.target.value)
  }

  const handleSubmit = async (message: string) => {
    messageRef.current = message
    setMessage('')
    await mutateAsync(message)
  }

  const handleClearChatHistory = async () => {
    setRemoveConfirm({
      handleConfirm: async () => {
        await clearHistory()
      },
      itemsTitles: [],
      itemsRelations: [],
      itemsType: t('confirm-modal-web-chatbot-history.v'),
    })
  }

  useImperativeHandle(ref, () => ({
    submitMessage: handleSubmit,
  }))

  return (
    <Box
      sx={{
        width: '100%',
        marginTop: '32px',
        marginBottom: '64px',
      }}
    >
      <TextField
        sx={{ mb: '8px' }}
        value={message}
        onChange={handleChange}
        className={classes.chatInput}
        variant="outlined"
        placeholder="Message Traicy"
        multiline
        minRows={1}
        maxRows={6}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Button
                onClick={handleSubmit.bind(null, message)}
                disabled={isGenerateResponceLoading || !message || message.length > 999}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '40px',
                  width: '40px',
                  p: 0,
                  borderRadius: '50%',
                  minWidth: '40px',
                }}
              >
                <SendIcon />
              </Button>
            </InputAdornment>
          ),
        }}
      />

      <Box
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
        }}
      >
        <Button
          sx={{
            fontSize: '10px',
            fontWeight: 600,
            color: theme.palette.custom.grey2,
          }}
          disabled={isClearHistoryLoading || isLoading || !data || data?.length === 0}
          onClick={handleClearChatHistory}
        >
          Delete Chat History
        </Button>
      </Box>

      {isGenerateResponceLoading && messageRef?.current && (
        <WebChatBotHistory
          data={data || []}
          isLoading={isLoading}
          dummyData={[
            { text: 'text', sender: 'assistant', created_at: new Date() },
            {
              text: messageRef.current,
              sender: 'user',
              created_at: new Date(),
            },
          ]}
        />
      )}

      <WebChatBotHistory data={data || []} isLoading={isLoading} />
    </Box>
  )
})
